import React, { useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import ServerSideAgGrid, { useGridServerSideDataSource } from '@client-shared/ui-server-side-agGrid';

import { gridPivotOptions } from './constants';
import { getColumnDefs } from './helpers';
import { getLocalStorageKey } from './localStorage';
import Routes from '../../routes';
import useGridApi from '../../shared/hooks/useGridApi.hook';
import useGridParamsAndLocalStorageOptions from '../../shared/hooks/useGridParamsAndLocalStorageOptions.hook';
import Spinner from '../../shared/spinner.component';
import {
  runCustomReport as runCustomReportApi,
  testCustomReport as testCustomReportApi,
} from '../../shared/customReports/api';

import AgGrid from '../agGrid/agGrid.component';

const dummy = x => x;

const CustomReportsDataGrid = ({
  columns,
  rows,
  types,
  isGridLoading,
  serverSide,
  sql,
  queryParams,
  reportId,
  reportSetId,
  updatesParamsAndLocalStorage,
  onGridReadyForExcelExport = dummy,
  filtersValues,
  disableLocalStorage,
}) => {
  const { gridApi, onGridReady: onGridReadyApi } = useGridApi();

  useEffect(() => {
    if (gridApi) {
      gridApi.sizeColumnsToFit();
    }
  }, [gridApi]);

  const { onGridReady: onGridReadyServerSideDataSource } = useGridServerSideDataSource({
    fetchData: async ({ limit, offset, sortModel, filterModel }) => {
      const params = {
        limit,
        offset,
        sortModel,
        filterModel,
        ...queryParams,
      };
      const { rows } = await (reportId
        ? runCustomReportApi({
          reportId,
          params,
        })
        : testCustomReportApi({
          sql,
          params,
        }));
      return rows;
    },
    initialRows: rows,
  });

  const onServerSideGridReady = useCallback(params => {
    onGridReadyApi(params);
    onGridReadyServerSideDataSource(params);
    onGridReadyForExcelExport(params);
  }, [
    onGridReadyApi,
    onGridReadyServerSideDataSource,
    onGridReadyForExcelExport,
  ]);

  const onRegularGridReady = useCallback(params => {
    onGridReadyApi(params);
    onGridReadyForExcelExport(params);
  }, [
    onGridReadyApi,
    onGridReadyForExcelExport,
  ]);

  const columnDefsToDisplay = useMemo(() => getColumnDefs({ columns, types, filtersValues }),
    [columns, types, filtersValues]
  );

  const gridParamsAndLocalStorageOptions = useGridParamsAndLocalStorageOptions({
    isActive: updatesParamsAndLocalStorage,
    pathname: Routes.reports.CUSTOM_REPORTS,
    keyname: getLocalStorageKey(reportId, reportSetId),
    disableLocalStorage,
  });

  const renderServerSideAgGrid = useCallback(() => rows.length
    ? Boolean(columns.length) && <ServerSideAgGrid
      columnDefs={columnDefsToDisplay}
      onGridReady={onServerSideGridReady}
      {...gridParamsAndLocalStorageOptions}
    /> : null, [
    columns,
    rows,
    columnDefsToDisplay,
    onServerSideGridReady,
    gridParamsAndLocalStorageOptions,
  ]);

  const renderAgGrid = useCallback(() => isGridLoading
    ? <Spinner />
    : Boolean(columns.length) && <AgGrid
      columnDefs={columnDefsToDisplay}
      rowData={rows}
      withSearchBar
      headerHeight={60}
      onGridReady={onRegularGridReady}
      suppressSizeColumnsToFit
      {...gridParamsAndLocalStorageOptions}
      {...gridPivotOptions}
    />, [
    columns,
    columnDefsToDisplay,
    rows,
    onRegularGridReady,
    isGridLoading,
    gridParamsAndLocalStorageOptions,
  ]);

  return (
    <div className="ag-grid-full-page-with-secondary-menu">
      { serverSide
        ? renderServerSideAgGrid()
        : renderAgGrid()
      }
    </div>
  );
};

CustomReportsDataGrid.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }),
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]))).isRequired,
  types: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ])).isRequired,
  isGridLoading: PropTypes.bool,
  serverSide: PropTypes.bool,
  updatesParamsAndLocalStorage: PropTypes.bool,
  reportId: PropTypes.string,
  reportSetId: PropTypes.string,
  sql: PropTypes.string,
  queryParams: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ])).isRequired,
  onGridReadyForExcelExport: PropTypes.func,
  filtersValues: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  disableLocalStorage: PropTypes.bool,
};

export default CustomReportsDataGrid;
